import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { workspaceApi, type MileageEntry } from '../api/workspace';
import { EmptyState, Loading, PageError } from '../components/State';
import { Modal } from '../components/Modal';
import { isApiError } from '../lib/apiClient';
import { fmtDate, money, todayIso } from '../lib/format';
import './InvoicesPage.css';
import './MileagePage.css';

const DEFAULT_RATE = 0.7;

export function MileagePage() {
  const [showAdd, setShowAdd] = useState(false);
  const qc = useQueryClient();

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['mileage'],
    queryFn: workspaceApi.mileage,
  });

  const del = useMutation({
    mutationFn: (id: string) => workspaceApi.deleteMileage(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mileage'] }),
  });

  const groups = useMemo(() => groupByMonth(data ?? []), [data]);
  const totalKm = (data ?? []).reduce((s, e) => s + e.distance, 0);
  const totalAmt = (data ?? []).reduce((s, e) => s + e.totalAmount, 0);

  if (isLoading) return <Loading />;

  return (
    <div>
      <div className="page-head">
        <h1>Mileage</h1>
        <button className="btn" onClick={() => setShowAdd(true)}>
          Add trip
        </button>
      </div>

      {isError ? (
        <PageError error={error} onRetry={() => refetch()} />
      ) : (data ?? []).length === 0 ? (
        <EmptyState text="No mileage logged yet." />
      ) : (
        <>
          <div className="inv__summary">
            <div>
              <div className="inv__summary-val">{totalKm.toFixed(1)} km</div>
              <div className="muted inv__summary-lbl">Total distance</div>
            </div>
            <div>
              <div className="inv__summary-val">{money(totalAmt)}</div>
              <div className="muted inv__summary-lbl">Total claim</div>
            </div>
          </div>

          {groups.map((g) => (
            <section key={g.key} className="mil__group">
              <div className="mil__group-head">
                <h2 style={{ margin: 0 }}>{g.label}</h2>
                <span className="muted">
                  {g.items.reduce((s, e) => s + e.distance, 0).toFixed(1)} km ·{' '}
                  {money(g.items.reduce((s, e) => s + e.totalAmount, 0))}
                </span>
              </div>
              <div className="card inv__list">
                {g.items.map((e) => (
                  <div key={e.id} className="mil__row">
                    <div className="mil__row-main">
                      <div className="mil__row-title">
                        {[e.startLocation, e.endLocation].filter(Boolean).join(' → ') || 'Trip'}
                      </div>
                      <div className="muted mil__row-sub">
                        {fmtDate(e.date)}
                        {e.purpose ? ` · ${e.purpose}` : ''} · {e.distance} km @ {e.ratePerKm}/km
                      </div>
                    </div>
                    <div className="mil__row-amt">{money(e.totalAmount)}</div>
                    <button
                      className="mil__del"
                      disabled={del.isPending}
                      onClick={() => {
                        if (confirm('Delete this mileage entry?')) del.mutate(e.id);
                      }}
                      aria-label="Delete"
                    >
                      🗑
                    </button>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </>
      )}

      {showAdd && <AddMileageModal onClose={() => setShowAdd(false)} />}
    </div>
  );
}

function AddMileageModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const clients = useQuery({ queryKey: ['clients'], queryFn: workspaceApi.clients });

  const [date, setDate] = useState(todayIso());
  const [startLocation, setStart] = useState('');
  const [endLocation, setEnd] = useState('');
  const [distance, setDistance] = useState('');
  const [purpose, setPurpose] = useState('');
  const [ratePerKm, setRate] = useState(String(DEFAULT_RATE));
  const [connectionId, setConnectionId] = useState('');

  const km = parseFloat(distance) || 0;
  const rate = parseFloat(ratePerKm) || 0;
  const preview = Math.round(km * rate * 100) / 100;

  const create = useMutation({
    mutationFn: () =>
      workspaceApi.createMileage({
        date,
        startLocation: startLocation.trim() || undefined,
        endLocation: endLocation.trim() || undefined,
        distance: km,
        purpose: purpose.trim() || undefined,
        ratePerKm: rate,
        connectionId: connectionId || undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mileage'] });
      onClose();
    },
  });

  const valid = date && km > 0 && rate > 0;

  return (
    <Modal
      title="Add trip"
      onClose={onClose}
      footer={
        <>
          <button className="btn btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button className="btn" disabled={!valid || create.isPending} onClick={() => create.mutate()}>
            {create.isPending ? 'Saving…' : `Save · ${money(preview)}`}
          </button>
        </>
      }
    >
      <div className="inv__two">
        <div className="field">
          <label>Date</label>
          <input type="date" max={todayIso()} value={date} onChange={(e) => setDate(e.target.value)} />
        </div>
        <div className="field">
          <label>Distance (km)</label>
          <input
            type="number"
            min={0}
            step="0.1"
            value={distance}
            onChange={(e) => setDistance(e.target.value)}
          />
        </div>
      </div>
      <div className="field">
        <label>From</label>
        <input value={startLocation} onChange={(e) => setStart(e.target.value)} placeholder="Start location" />
      </div>
      <div className="field">
        <label>To</label>
        <input value={endLocation} onChange={(e) => setEnd(e.target.value)} placeholder="End location" />
      </div>
      <div className="inv__two">
        <div className="field">
          <label>Rate per km</label>
          <input
            type="number"
            min={0}
            step="0.01"
            value={ratePerKm}
            onChange={(e) => setRate(e.target.value)}
          />
        </div>
        <div className="field">
          <label>Family (optional)</label>
          <select value={connectionId} onChange={(e) => setConnectionId(e.target.value)}>
            <option value="">Not linked</option>
            {(clients.data ?? []).map((c) => (
              <option key={c.connectionId} value={c.connectionId}>
                {c.youthSnapshot?.name ?? 'Youth'}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="field">
        <label>Purpose</label>
        <input value={purpose} onChange={(e) => setPurpose(e.target.value)} placeholder="e.g. School pickup" />
      </div>

      {create.isError && (
        <p className="error-text">
          {isApiError(create.error) ? create.error.message : 'Could not save the trip.'}
        </p>
      )}
    </Modal>
  );
}

function groupByMonth(entries: MileageEntry[]) {
  const sorted = [...entries].sort((a, b) => b.date.localeCompare(a.date));
  const out: { key: string; label: string; items: MileageEntry[] }[] = [];
  for (const e of sorted) {
    const key = e.date.slice(0, 7);
    const label = new Date(`${key}-01T00:00:00`).toLocaleDateString(undefined, {
      month: 'long',
      year: 'numeric',
    });
    const last = out[out.length - 1];
    if (last && last.key === key) last.items.push(e);
    else out.push({ key, label, items: [e] });
  }
  return out;
}
